import CustomDatatable from '../../libs/datatables/custom_datatable'

const { $ } = window as any

const NON_NAVIGABLE_SELECTOR = 'a, button, input, textarea, select, label, .pswp-img-link, td.actions, td.element-table-photos, td.gallery'
const ROW_CHECKBOX_SELECTOR = '.js-common-elements-row-checkbox'
const SELECT_ALL_SELECTOR = '.js-common-elements-select-all'
const BATCH_FORM_SELECTOR = '.js-common-elements-batch-form'
const BATCH_IDS_CONTAINER_SELECTOR = '.js-common-elements-selected-ids'
const BATCH_SUBMIT_SELECTOR = '.js-common-elements-batch-submit'
const OPEN_BATCH_ACTIONS_SELECTOR = '.js-common-elements-open-batch-actions'
const EXPORT_SELECTED_SELECTOR = '.js-common-elements-export-selected'
const DOWNLOAD_IMAGES_SELECTOR = '.js-common-elements-download-images'

const selectedIdsArray = (selectedIds) => Array.from(selectedIds).sort((a, b) => a - b)

const restoreFilterValues = ($table, state) => {
  if (!state || !state.columns) {
    return
  }

  $table.find('thead tr.filters th').each((index, element) => {
    const value = state.columns[index]?.search?.search
    if (value === undefined) {
      return
    }

    $('input[type="text"]', element).val(value)
  })
}

const bindColumnFilters = ($table, datatable) => {
  datatable.columns().header().each((headerCell, index) => {
    const filterCell = $('thead tr.filters th', $table).get(index)
    if (!filterCell) {
      return
    }

    const input = $('input', filterCell).get(0)
    if (!input) {
      return
    }

    $(input).on('keyup change', function () {
      datatable.column(index).search(this.value, false, true).draw()
    })

    $(input).on('click', (e) => e.stopPropagation())
  })
}

const checkboxId = (checkbox) => Number($(checkbox).val())

const filteredRowCheckboxes = (datatable) => {
  return $(datatable.rows({ search: 'applied' }).nodes()).find(ROW_CHECKBOX_SELECTOR)
}

const syncRowCheckboxes = ($table, selectedIds) => {
  $table.find(ROW_CHECKBOX_SELECTOR).each((_index, checkbox) => {
    const id = checkboxId(checkbox)
    if (!id) {
      return
    }

    $(checkbox).prop('checked', selectedIds.has(id))
  })
}

const syncSelectAllState = ($table, datatable, selectedIds) => {
  const $selectAll = $table.find(SELECT_ALL_SELECTOR)
  if ($selectAll.length === 0) {
    return
  }

  const $visibleCheckboxes = filteredRowCheckboxes(datatable)
  if ($visibleCheckboxes.length === 0) {
    $selectAll.prop('checked', false)
    $selectAll.prop('indeterminate', false)
    return
  }

  let selectedVisibleCount = 0
  $visibleCheckboxes.each((_index, checkbox) => {
    const id = checkboxId(checkbox)
    if (id && selectedIds.has(id)) {
      selectedVisibleCount += 1
    }
  })

  const allSelected = selectedVisibleCount > 0 && selectedVisibleCount === $visibleCheckboxes.length
  const partiallySelected = selectedVisibleCount > 0 && selectedVisibleCount < $visibleCheckboxes.length
  $selectAll.prop('checked', allSelected)
  $selectAll.prop('indeterminate', partiallySelected)
}

const syncBatchFormState = (selectedIds) => {
  const $form = $(BATCH_FORM_SELECTOR)
  if ($form.length === 0) {
    return
  }

  const $idsContainer = $form.find(BATCH_IDS_CONTAINER_SELECTOR)
  $idsContainer.empty()

  selectedIdsArray(selectedIds).forEach((id) => {
    $('<input/>', { type: 'hidden', name: 'ids[]', value: id }).appendTo($idsContainer)
  })

  $form.find(BATCH_SUBMIT_SELECTOR).prop('disabled', selectedIds.size === 0)
}

const syncOpenBatchActionsState = (selectedIds) => {
  $(OPEN_BATCH_ACTIONS_SELECTOR).prop('disabled', selectedIds.size === 0)
}

const bindBatchTaggingControls = ($table, datatable) => {
  // The batch-tag form only renders for users who may update elements; the rest
  // of the selection UI (export, download) is available to any reader, so this
  // must keep binding when the form is absent.
  const $form = $(BATCH_FORM_SELECTOR)

  const selectedIds = new Set<number>()

  const syncSelectionUI = () => {
    syncRowCheckboxes($table, selectedIds)
    syncSelectAllState($table, datatable, selectedIds)
    syncBatchFormState(selectedIds)
    syncOpenBatchActionsState(selectedIds)
  }

  $table.on('change', ROW_CHECKBOX_SELECTOR, (event) => {
    const id = checkboxId(event.currentTarget)
    if (!id) {
      return
    }

    if ($(event.currentTarget).is(':checked')) {
      selectedIds.add(id)
    } else {
      selectedIds.delete(id)
    }

    syncSelectionUI()
  })

  $table.on('change', SELECT_ALL_SELECTOR, (event) => {
    const shouldSelect = $(event.currentTarget).is(':checked')
    filteredRowCheckboxes(datatable).each((_index, checkbox) => {
      const id = checkboxId(checkbox)
      if (!id) {
        return
      }

      if (shouldSelect) {
        selectedIds.add(id)
      } else {
        selectedIds.delete(id)
      }
    })

    syncSelectionUI()
  })

  $form.on('submit', (event) => {
    syncBatchFormState(selectedIds)
    if (selectedIds.size === 0) {
      event.preventDefault()
    }
  })

  const bindBatchPostAction = (selector: string) => {
    $(document).on('click', selector, (event) => {
      event.preventDefault()

      if (selectedIds.size === 0) {
        return
      }

      const url = $(event.currentTarget).data('url')
      if (!url) {
        return
      }

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        data: { ids: selectedIdsArray(selectedIds) },
      }).fail((request) => {
        const message = request.responseJSON?.message
        if (message) {
          window.toastr.error(message)
        }
      })
    })
  }

  bindBatchPostAction(EXPORT_SELECTED_SELECTOR)
  bindBatchPostAction(DOWNLOAD_IMAGES_SELECTOR)

  datatable.on('draw', () => {
    syncSelectionUI()
  })

  syncSelectionUI()
}

export default function commonElementsIndex() {
  $(document).on('ready', () => {
    const $table = $('#common-elements-table')
    if ($table.length === 0) {
      return
    }

    const customDatatable = new CustomDatatable()
    const defaultOrderColumn = $table.find('thead tr:first th.js-dt-batch-select').length > 0 ? 1 : 0
    const options = $.extend(customDatatable.defaultOptions(), {
      order: [[defaultOrderColumn, 'asc']],
      orderCellsTop: true,
      stateLoaded: (_settings, state) => {
        restoreFilterValues($table, state)
      },
    })

    const datatable = $table.DataTable(options)
    bindColumnFilters($table, datatable)
    bindBatchTaggingControls($table, datatable)

    const selector = 'tbody tr'
    $table.on('click', selector, (e) => {
      if ($(e.target).closest(NON_NAVIGABLE_SELECTOR).length > 0) {
        return
      }

      const url = $(e.currentTarget).data('url')
      if (url) {
        window.location.href = url
      }
    })
  })
}
